import { useAuth } from "@/contexts/AuthContext";
import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import LoadingSpinner from "@/components/orders/LoadingSpinner";

interface RoleGuardProps {
  children: ReactNode;
  allowedRoles: string[];
  fallbackRoute?: string;
  showMessage?: boolean;
}

export function RoleGuard({
  children,
  allowedRoles,
  fallbackRoute = "/unauthorized",
  showMessage = true,
}: RoleGuardProps) {
  const { userRole, isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while auth is loading
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // Redirect to welcome if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/welcome" replace />;
  }

  // Show loading spinner while userRole is still being determined
  // (authenticated but role not yet determined)
  if (isAuthenticated && userRole === null) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // Now we can safely check if the user has the required role
  // (at this point userRole should not be null, but add safety check for TypeScript)
  if (!userRole || !allowedRoles.includes(userRole)) {
    if (showMessage) {
      return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
          <div className="max-w-md p-8 bg-white rounded-lg shadow-lg text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Acceso Restringido
            </h2>
            <p className="text-gray-600 mb-6">
              No tienes permisos para acceder a esta sección de la aplicación.
            </p>
            <div className="space-y-3">
              <button
                onClick={() => window.history.back()}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Volver
              </button>
            </div>
          </div>
        </div>
      );
    }
    return <Navigate to={fallbackRoute} replace />;
  }

  return <>{children}</>;
}
